import { useState, useEffect, useCallback } from 'react';
import { Shield, MapPin, Clock, School, X, AlertCircle, RefreshCw, Search } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AppHeader } from '@/components/layout/AppHeader';

interface PrivacySettings {
  blockedDistricts: Array<{ id: string; name: string; state: string }>;
  blockedZipCodes: string[];
  blockRadius: number;
  schoolHoursInvisibility: {
    enabled: boolean;
    startTime: string;
    endTime: string;
    timezone: string;
  };
  currentLocation?: {
    latitude: number;
    longitude: number;
  };
}

export default function PrivacySettingsPage() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [newZipCode, setNewZipCode] = useState('');
  const [schoolHoursEnabled, setSchoolHoursEnabled] = useState(false);
  const [startTime, setStartTime] = useState('07:30');
  const [endTime, setEndTime] = useState('15:30');
  const [blockRadius, setBlockRadius] = useState(10);
  const [districtSearch, setDistrictSearch] = useState('');
  const [districtResults, setDistrictResults] = useState<Array<{ id: string; name: string; state: string; city: string }>>([]);

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';

  // Fetch current privacy settings
  const { data: settings, isLoading, error } = useQuery<PrivacySettings>({
    queryKey: ['privacySettings'],
    queryFn: async () => {
      const token = await getToken();
      const response = await fetch(`${apiUrl}/api/privacy/settings`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch privacy settings');
      const data = await response.json();
      return data.settings; // Unwrap settings from API response
    },
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
  });

  // Update privacy settings mutation
  const updateSettings = useMutation({
    mutationFn: async (updates: any) => {
      const token = await getToken();
      const response = await fetch(`${apiUrl}/api/privacy/settings`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      if (!response.ok) throw new Error('Failed to update privacy settings');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['privacySettings'] });
      toast.success('Privacy settings updated');
    },
    onError: (error) => {
      toast.error('Failed to update settings');
      console.error('Update error:', error);
    },
  });

  // Reset settings mutation
  const resetSettings = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const response = await fetch(`${apiUrl}/api/privacy/settings`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to reset privacy settings');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['privacySettings'] });
      toast.success('Privacy settings reset to defaults');
      setResetDialogOpen(false);
    },
    onError: (error) => {
      toast.error('Failed to reset settings');
      console.error('Reset error:', error);
    },
  });

  // Initialize form values when settings are loaded
  useEffect(() => {
    if (settings) {
      setSchoolHoursEnabled(settings.schoolHoursInvisibility?.enabled || false);
      setStartTime(settings.schoolHoursInvisibility?.startTime || '07:30');
      setEndTime(settings.schoolHoursInvisibility?.endTime || '15:30');
      setBlockRadius(settings.blockRadius || 10);
    }
  }, [settings]);

  const handleRemoveDistrict = (districtId: string) => {
    if (!settings) return;

    const updatedDistricts = settings.blockedDistricts.filter(d => d.id !== districtId);
    updateSettings.mutate({
      blockedDistricts: updatedDistricts.map(d => d.id),
    });
  };

  const handleDistrictSearch = useCallback(async (query: string) => {
    setDistrictSearch(query);
    if (query.length < 2) {
      setDistrictResults([]);
      return;
    }

    try {
      const token = await getToken();
      const response = await fetch(`${apiUrl}/api/privacy/districts?q=${encodeURIComponent(query)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setDistrictResults(data.districts || []);
      }
    } catch (error) {
      console.error('District search failed:', error);
    }
  }, [getToken, apiUrl]);

  const handleAddDistrict = (district: { id: string; name: string; state: string }) => {
    if (!settings) return;

    const alreadyBlocked = settings.blockedDistricts.some(d => d.id === district.id);
    if (alreadyBlocked) {
      toast.error('This district is already blocked');
      return;
    }

    const updatedDistricts = [...settings.blockedDistricts, district];
    updateSettings.mutate({
      blockedDistricts: updatedDistricts.map(d => d.id),
    });
    setDistrictSearch('');
    setDistrictResults([]);
  };

  const handleRemoveZipCode = (zipCode: string) => {
    if (!settings) return;

    const updatedZipCodes = settings.blockedZipCodes.filter(z => z !== zipCode);
    updateSettings.mutate({
      blockedZipCodes: updatedZipCodes,
    });
  };

  const handleAddZipCode = () => {
    if (!newZipCode || !/^\d{5}$/.test(newZipCode)) {
      toast.error('Please enter a valid 5-digit ZIP code');
      return;
    }

    if (settings?.blockedZipCodes.includes(newZipCode)) {
      toast.error('This ZIP code is already blocked');
      return;
    }

    const updatedZipCodes = [...(settings?.blockedZipCodes || []), newZipCode];
    updateSettings.mutate({
      blockedZipCodes: updatedZipCodes,
    });
    setNewZipCode('');
  };

  const handleSchoolHoursUpdate = () => {
    updateSettings.mutate({
      schoolHoursInvisibility: {
        enabled: schoolHoursEnabled,
        startTime,
        endTime,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });
  };

  const handleRadiusUpdate = () => {
    updateSettings.mutate({
      blockRadius,
    });
  };

  const handleResetConfirm = () => {
    resetSettings.mutate();
  };

  const calculatePrivacyScore = () => {
    if (!settings) return 'Unknown';

    let score = 0;
    if (settings.blockedDistricts.length > 0) score += 30;
    if (settings.blockedZipCodes.length > 0) score += 20;
    if (settings.blockRadius > 0) score += 25;
    if (settings.schoolHoursInvisibility?.enabled) score += 25;

    if (score >= 75) return 'High';
    if (score >= 50) return 'Medium';
    if (score > 0) return 'Low';
    return 'None';
  };

  const privacyScore = calculatePrivacyScore();
  const scoreColor = {
    High: 'text-emerald-400',
    Medium: 'text-yellow-400',
    Low: 'text-orange-400',
    None: 'text-red-400',
    Unknown: 'text-brand-muted',
  }[privacyScore];

  if (isLoading) {
    return (
      <div className="container mx-auto p-4 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <Card className="bg-brand-surface border-red-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5" />
              <p>Failed to load privacy settings. Please try again.</p>
            </div>
            <Button
              onClick={() => queryClient.invalidateQueries({ queryKey: ['privacySettings'] })}
              className="mt-4 bg-brand-primary hover:bg-brand-primary/90 text-brand-surface"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <AppHeader />
      <div className="container mx-auto p-4 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-brand-primary to-pink-400 bg-clip-text text-transparent">
            Privacy & Safety Settings
          </h1>
        <p className="text-brand-muted text-lg">
          Control who can see your profile and when you're visible
        </p>
        <div className="mt-6 p-4 bg-brand-surface rounded-lg border border-brand-border flex items-center gap-3">
          <Shield className="w-6 h-6 text-brand-primary" />
          <span className="text-brand-text">Privacy Protection Level:</span>
          <span className={`font-bold text-lg ${scoreColor}`}>{privacyScore}</span>
        </div>
      </div>

      <div className="space-y-6">
        {/* Blocked Districts */}
        <Card className="bg-brand-surface border-brand-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-brand-text">
              <School className="w-5 h-5 text-brand-primary" />
              Blocked School Districts
            </CardTitle>
            <CardDescription className="text-brand-muted">
              Teachers from these districts cannot see your profile
            </CardDescription>
          </CardHeader>
          <CardContent>
            {settings?.blockedDistricts.length === 0 ? (
              <p className="text-brand-muted text-sm">No districts blocked</p>
            ) : (
              <div className="space-y-2">
                {settings?.blockedDistricts.map(district => (
                  <div key={district.id} className="flex items-center justify-between p-2 rounded-lg bg-brand-background/50">
                    <span className="text-brand-text">{district.name}, {district.state}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveDistrict(district.id)}
                      className="hover:bg-brand-border text-brand-primary"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* District Search */}
            <div className="mt-4 space-y-2 relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-brand-muted" />
                <Input
                  type="text"
                  placeholder="Search school districts..."
                  value={districtSearch}
                  onChange={(e) => handleDistrictSearch(e.target.value)}
                  className="pl-10 bg-brand-background border-brand-border text-brand-text placeholder:text-brand-muted"
                />
              </div>

              {/* Search Results Dropdown */}
              {districtResults.length > 0 && (
                <div className="absolute z-50 w-full bg-brand-background border border-brand-border rounded-lg max-h-48 overflow-y-auto shadow-lg">
                  {districtResults.map((district) => (
                    <div
                      key={district.id}
                      onClick={() => handleAddDistrict(district)}
                      className="p-3 hover:bg-brand-surface cursor-pointer border-b border-brand-border last:border-b-0 transition-colors"
                    >
                      <div className="text-brand-text font-medium">{district.name}</div>
                      <div className="text-sm text-brand-muted">{district.city}, {district.state}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Blocked ZIP Codes */}
        <Card className="bg-brand-surface border-brand-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-brand-text">
              <MapPin className="w-5 h-5 text-brand-primary" />
              Blocked ZIP Codes
            </CardTitle>
            <CardDescription className="text-brand-muted">
              Hide your profile from users in these areas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 mb-4">
              {settings?.blockedZipCodes.length === 0 ? (
                <p className="text-brand-muted text-sm">No ZIP codes blocked</p>
              ) : (
                settings?.blockedZipCodes.map(zip => (
                  <Badge
                    key={zip}
                    variant="secondary"
                    className="gap-1 bg-brand-primary/20 text-brand-primary border border-brand-primary/30 hover:bg-brand-primary/30"
                  >
                    {zip}
                    <X
                      className="w-3 h-3 cursor-pointer hover:text-brand-text"
                      onClick={() => handleRemoveZipCode(zip)}
                    />
                  </Badge>
                ))
              )}
            </div>
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Enter ZIP code"
                value={newZipCode}
                onChange={(e) => setNewZipCode(e.target.value)}
                maxLength={5}
                className="w-32 bg-brand-background border-brand-border text-brand-text placeholder:text-brand-muted"
              />
              <Button
                onClick={handleAddZipCode}
                className="bg-brand-primary hover:bg-brand-primary/90 text-brand-surface"
              >
                Add ZIP
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Block Radius */}
        <Card className="bg-brand-surface border-brand-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-brand-text">
              <MapPin className="w-5 h-5 text-brand-primary" />
              Location Block Radius
            </CardTitle>
            <CardDescription className="text-brand-muted">
              Hide from users within this distance of your location
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Label htmlFor="radius" className="text-brand-text">Radius (miles):</Label>
                <Input
                  id="radius"
                  type="number"
                  min="0"
                  max="100"
                  value={blockRadius}
                  onChange={(e) => setBlockRadius(Number(e.target.value))}
                  className="w-24 bg-brand-background border-brand-border text-brand-text"
                />
                <Button
                  onClick={handleRadiusUpdate}
                  className="bg-brand-primary hover:bg-brand-primary/90 text-brand-surface"
                >
                  Update
                </Button>
              </div>
              {blockRadius > 0 && (
                <p className="text-sm text-brand-muted px-3 py-2 bg-brand-background/50 rounded-lg border border-brand-border">
                  Blocking users within {blockRadius} miles of your location
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* School Hours Invisibility */}
        <Card className="bg-brand-surface border-brand-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-brand-text">
              <Clock className="w-5 h-5 text-brand-primary" />
              School Hours Invisibility
            </CardTitle>
            <CardDescription className="text-brand-muted">
              Automatically hide your profile during school hours
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-brand-background/50 rounded-lg">
                <Label htmlFor="school-hours" className="text-brand-text cursor-pointer">
                  Enable school hours hiding
                </Label>
                <Switch
                  id="school-hours"
                  checked={schoolHoursEnabled}
                  onCheckedChange={setSchoolHoursEnabled}
                  className="data-[state=checked]:bg-brand-primary"
                />
              </div>

              {schoolHoursEnabled && (
                <div className="grid grid-cols-2 gap-4 p-4 bg-brand-background/30 rounded-lg border border-brand-border">
                  <div>
                    <Label htmlFor="start-time" className="text-brand-text">Start Time</Label>
                    <Input
                      id="start-time"
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="bg-brand-background border-brand-border text-brand-text"
                    />
                  </div>
                  <div>
                    <Label htmlFor="end-time" className="text-brand-text">End Time</Label>
                    <Input
                      id="end-time"
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      className="bg-brand-background border-brand-border text-brand-text"
                    />
                  </div>
                </div>
              )}

              <Button
                onClick={handleSchoolHoursUpdate}
                className="bg-brand-primary hover:bg-brand-primary/90 text-brand-surface"
              >
                Save School Hours
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Reset Settings */}
        <Card className="bg-brand-surface border-red-500/30">
          <CardHeader>
            <CardTitle className="text-red-400">Reset Privacy Settings</CardTitle>
            <CardDescription className="text-brand-muted">
              Remove all privacy protections and return to default settings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              onClick={() => setResetDialogOpen(true)}
              className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
            >
              Reset to Defaults
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Privacy Settings?</DialogTitle>
            <DialogDescription>
              Are you sure? This will remove all privacy protections including:
              <ul className="list-disc list-inside mt-2">
                <li>All blocked school districts</li>
                <li>All blocked ZIP codes</li>
                <li>Location block radius</li>
                <li>School hours invisibility</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleResetConfirm}>
              Reset All Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </>
  );
}